import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ModalComponent } from '../../base2';
import {
  Button,
  ButtonBar,
  ConfirmationModal,
  ModalFormHeader,
  PersonInfoDisplay,
  ScreenHeader
} from '../../components';
import { WillAssetSummary } from '../../components/v2/will-asset-summary';
import { WillDebtors } from '../../components/v2/will-debtors';
import { WillExecutors } from '../../components/v2/will-executors';
import { WillPetArrangements } from '../../components/v2/will-pet-arrangements';
import {
  AssetsProvider,
  DebtorsProvider,
  ExecutorsProvider,
  PetArrangementsProvider
} from '../../context';
import { WillTemplate } from '../../demo-components/will-template';
import { useDelete, useShow } from '../../hooks';
import { NewWillContext } from '../new-will';

interface SummaryScreenProps {
  isContinue?: boolean;
}

export function NewWillSummaryScreen({ isContinue = false }: SummaryScreenProps) {
  const navigate = useNavigate();

  const [ showModal, setShowModal ] = useState(false);
  const [ deleteModal, setDeleteModal ] = useState({ resource: '', id: '' });

  const { will, update } = React.useContext(NewWillContext);
  const { entity: primaryBeneficiary, handleShow } = useShow('people');

  const { handleDelete: handleDeleteAsset } = useDelete('assets');

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, [ setShowModal ]);

  // Fetch primary beneficiary data if primaryBeneficiaryId exists
  useEffect(() => {
    if (will?.primaryBeneficiaryId) {
      handleShow(will.primaryBeneficiaryId);
    }
  }, [ will?.primaryBeneficiaryId, handleShow ]);

  const handleCancel = useCallback(() => {
    if (isContinue) {
      navigate('/home');
    }
    
    return navigate('../executors');
  }, [ isContinue, navigate ]);

  const handleNext = useCallback(() => {
    navigate('../asset-distribution');
  }, [ navigate ]);

  const handleEditAsset = useCallback((id: string) => {
    navigate(`../add-asset?edit=${id}`);
  }, [ navigate ]);

  const handleShowDeleteAssetModal = useCallback((id: string) => {
    console.log('handleDelete');
    setDeleteModal({ resource: 'assets', id: id });
  }, []);

  const handleConfirmDeleteAsset = useCallback(
    async () => {
      console.log('handleRemoveAsset', deleteModal.id);
      await handleDeleteAsset(deleteModal.id);
      await update();
    }, 
    [ deleteModal.id, handleDeleteAsset, update ]
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ resource: '', id: '' });
  }, []);

  const subtitle = isContinue ? 'Continue from where you left off...' : 'Review your choices';

  return (
    <div className="new-will-summary-screen screen">
      <ScreenHeader
        title="Summary"
        description={subtitle}
      />

      {/* Primary Beneficiary Section */}
      {primaryBeneficiary && (
        <div className="form-container">
          <ModalFormHeader
            icon="savings"
            header="Primary Beneficiary"
          />
          <PersonInfoDisplay person={primaryBeneficiary} />
        </div>
      )}

      <div className="summary-content">
        <AssetsProvider>
          <WillAssetSummary handleRemoveAsset={handleShowDeleteAssetModal} handleEditAsset={handleEditAsset} />
        </AssetsProvider>

        <PetArrangementsProvider>
          <WillPetArrangements />
        </PetArrangementsProvider>

        <DebtorsProvider>
          <WillDebtors />
        </DebtorsProvider>
        
        <ExecutorsProvider>
          <WillExecutors />
        </ExecutorsProvider>
      </div>

      <ModalComponent
        showModal={showModal}
        closeModal={closeModal}
      >
        <WillTemplate />
      </ModalComponent>

      <ModalComponent
        showModal={!!deleteModal.resource}
        closeModal={closeDeleteModal}
      >
        <ConfirmationModal
          message="Are you sure you want to delete this asset?"
          onConfirm={handleConfirmDeleteAsset}
          onCancel={closeDeleteModal}
          confirmText="Delete"
          cancelText="Cancel"
          icon="warning"
        />
      </ModalComponent>

      <Button 
        onClick={useCallback(() => setShowModal(true), [])}
        fullWidth
      >
        Preview Will
      </Button>

      <ButtonBar 
        handlePrimary={handleNext} 
        handleSecondary={handleCancel} 
        primaryText={isContinue ? 'Continue' : 'Finish'}
      />
    </div>
  );
}
